import { createListenerMiddleware, isAnyOf, type UnknownAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/root-state';
import { patchScheduledEmailCustomizations } from '@/store/slices/scheduled-emails';
import {
  deleteBlock,
  insertBlock,
  loadCampaignEditor,
  moveBlock,
  reorderBlocks,
  resetCampaignEditor,
  setCampaignEditorBlocksForTemplate,
  setCampaignEditorField,
  setCampaignEditorTemplateMounted,
  updateBlockImage,
  updateBlockProp,
  updateBlockStyle,
  updateBlockValue,
} from '..';
import { buildCustomizationsPatch, editorBlocksFor, editorContentSignature, editorEventTitle } from '../helpers';
import {
  selectCampaignEditorContent,
  selectCampaignEditorCurrentTemplateId,
  selectCampaignEditorEmail,
  selectCampaignEditorTheme,
} from '../selectors';

const SYNC_DEBOUNCE_MS = 750;

const campaignEditorMiddleware = createListenerMiddleware();

// built on first use rather than at import - the slice's actions aren't defined yet while the
// store's own import cycle (scheduled-emails -> connection -> store) is still resolving
let matchers: { isEdit: (action: unknown) => boolean; isFlush: (action: unknown) => boolean } | null = null;
const getMatchers = () =>
  (matchers ??= {
    isEdit: isAnyOf(
      loadCampaignEditor.fulfilled,
      setCampaignEditorField,
      setCampaignEditorBlocksForTemplate,
      setCampaignEditorTemplateMounted,
      insertBlock,
      updateBlockValue,
      updateBlockImage,
      updateBlockProp,
      updateBlockStyle,
      moveBlock,
      reorderBlocks,
      deleteBlock
    ),
    // leaving the campaign (or loading it in another language) writes out an edit still waiting
    // on the debounce, from the state it was made in, rather than dropping it
    isFlush: isAnyOf(resetCampaignEditor, loadCampaignEditor.pending),
  });

// set while an edit is waiting on the debounce
let syncPending = false;

// the editor's content as it was loaded off the email, and whether anything has been saved for the
// campaign since. Together they keep the sync from writing an untouched campaign straight back out -
// that save bumps modified_at and drops the project into draft just for opening the editor
let loadedSignature: string | null = null;
let savedSinceLoad = false;

const syncCustomizations = (state: RootState, dispatch: (action: UnknownAction) => unknown) => {
  const editor = state.campaignEditor;
  const content = selectCampaignEditorContent(state);
  if (!editor.initialized || !editor.campaignUuid || !editor.templateMounted || !content) return;

  // loading the campaign, mounting its template and the theme packs arriving all run the sync with
  // values the user never touched, so hold the save until something actually differs from what was
  // loaded. Once a save has gone out the comparison is dropped - editing a field and undoing it has
  // to persist the undo
  if (!savedSinceLoad && editorContentSignature(editor) === loadedSignature) return;

  dispatch(
    patchScheduledEmailCustomizations({
      uuid: editor.campaignUuid,
      fields: buildCustomizationsPatch(editor, content, selectCampaignEditorTheme(state)),
    }) as unknown as UnknownAction
  );
};

// sync the editor's fields back into the scheduled email's working_customizations in the store
campaignEditorMiddleware.startListening({
  // the theme resolves from the loaded packs, so it re-syncs when they arrive as well as on edits
  predicate: (action, currentState, previousState) =>
    getMatchers().isEdit(action) ||
    getMatchers().isFlush(action) ||
    selectCampaignEditorTheme(currentState as RootState) !== selectCampaignEditorTheme(previousState as RootState),
  effect: async (action, listenerApi) => {
    listenerApi.cancelActiveListeners();

    if (getMatchers().isFlush(action)) {
      if (syncPending) syncCustomizations(listenerApi.getOriginalState() as RootState, listenerApi.dispatch);
      syncPending = false;
      return;
    }

    syncPending = true;
    await listenerApi.delay(SYNC_DEBOUNCE_MS);
    syncPending = false;
    syncCustomizations(listenerApi.getState() as RootState, listenerApi.dispatch);
  },
});

// remember what was loaded, so the sync above can tell an edit from the load itself
campaignEditorMiddleware.startListening({
  predicate: (action) => loadCampaignEditor.fulfilled.match(action),
  effect: (_action, listenerApi) => {
    loadedSignature = editorContentSignature((listenerApi.getState() as RootState).campaignEditor);
    savedSinceLoad = false;
  },
});

// any save for the campaign being edited - the sync's own, or a template switch patching its merged
// blocks - means the stored copy may no longer match what was loaded
campaignEditorMiddleware.startListening({
  predicate: (action, currentState) =>
    patchScheduledEmailCustomizations.pending.match(action) &&
    action.meta.arg.uuid === (currentState as RootState).campaignEditor.campaignUuid,
  effect: () => {
    savedSinceLoad = true;
  },
});

// the stored email moved to another template. selectCampaignTemplate patches the merged blocks
// alongside the new templateId, so the store already holds this template's blocks with the user's
// own copy carried across. The fallback covers a templateId that changed some other way (a second
// tab, a reload mid-switch).
campaignEditorMiddleware.startListening({
  predicate: (_action, currentState) => {
    const state = currentState as RootState;
    return (
      state.campaignEditor.initialized &&
      !!selectCampaignEditorEmail(state) &&
      selectCampaignEditorCurrentTemplateId(state) !== state.campaignEditor.templateId
    );
  },
  effect: (_action, listenerApi) => {
    const state = listenerApi.getState() as RootState;
    const { language, baseLanguage } = state.campaignEditor;
    const templateId = selectCampaignEditorCurrentTemplateId(state);

    listenerApi.dispatch(
      setCampaignEditorBlocksForTemplate({
        templateId,
        blocks: editorBlocksFor(
          selectCampaignEditorContent(state)?.blocks,
          templateId ?? '',
          editorEventTitle(state.project.workingProject, language ?? undefined),
          language ?? undefined,
          baseLanguage
        ),
      })
    );
  },
});

export default campaignEditorMiddleware;
